import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronDown, Home, Loader2, Lock, MailCheck } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/store/auth';
import { api, ApiError, NETWORK_MESSAGE } from '@/lib/api';
import { googleAvailable } from '@/lib/google';
import { clearToken, loadTokenBrand, tokenPending, useBrand } from '@/lib/brand';
import { afterLoginPath, safeNext } from '@/lib/brandSources';
import { EMAIL_RE, suggestEmail, useLeadIntake, withPrefix, type LeadIntake } from '@/lib/leadIntake';
import { BrandBand } from '@/components/BrandBand';
import { Turnstile, turnstileSiteKey } from '@/tour/Turnstile';

/**
 * The portal's front door: one email field and one button, "send me the link".
 *
 * - Opened with the org hint of an office whose prospect pages are published
 *   (prospect mode): it speaks to someone who wants to live there, takes a new
 *   lead's email with the office's privacy notice and the separate marketing
 *   opt-in, and the same field still works for people who already live there.
 * - Anything else: it re-sends the personal link to people the office has.
 *
 * Never "sign in", "register", "account" or "password": the visitor asks for a
 * link and gets one. The answer is the same whoever the email belongs to.
 */

/**
 * Where the building's band will be, while its brand is still on the way: a
 * neutral block of the same size, never TendedLoop's logo that would then swap.
 */
function BandSkeleton() {
  return (
    <div className="mb-6" aria-hidden data-testid="band-skeleton">
      <div className="flex min-h-[240px] animate-pulse items-center justify-center bg-slate-100 px-6 pb-10 pt-12">
        <div className="h-16 w-[200px] max-w-full rounded-xl bg-slate-200" />
      </div>
    </div>
  );
}

type Situation = 'prospect' | 'resident' | 'expired' | 'invalid';

const COPY: Record<Situation, { title: string; lead: string }> = {
  prospect: {
    title: 'רוצים לשמוע עוד על הדירות?',
    lead: 'השאירו מייל, ונשלח לכם קישור אישי: מה כלול בדירות, תיאום סיור בזמן שנוח לכם ודרך ישירה לצוות.',
  },
  resident: {
    title: 'הקישור האישי שלכם לפורטל',
    lead: 'הזינו את המייל שמסרתם לצוות הדיור, ונשלח אליו קישור שפותח את הפורטל.',
  },
  expired: {
    title: 'הקישור הקודם נסגר',
    lead: 'לשמירה על הפרטים שלכם, הפורטל נסגר אחרי זמן מה. הזינו את המייל ונשלח לכם קישור חדש.',
  },
  invalid: {
    title: 'הקישור הזה כבר לא פעיל',
    lead: 'קישור אישי פג אחרי זמן מה, או כשנשלח קישור חדש יותר. הזינו את המייל ונשלח לכם חדש.',
  },
};

const GOOGLE_ERRORS: Record<string, string> = {
  RESIDENT_NOT_INVITED: 'כתובת ה-Google הזו לא מוכרת לצוות הדיור. הזינו כאן את המייל שמסרתם להם, ונשלח אליו קישור.',
  RESIDENT_USE_EMAIL_LINK: 'הכתובת הזו מוכרת ביותר ממקום מגורים אחד. הזינו אותה כאן, ונשלח קישור לכל מקום בנפרד.',
};
const GOOGLE_FAILED = 'זה לא הצליח עם Google. נסו שוב, או הזינו כאן את המייל ונשלח לכם קישור.';

/** "Continue with Google": the email Google vouches for opens the portal at once. */
function GoogleOption({
  lead,
  onSignedIn,
}: {
  lead?: { consentVersion: string; marketingOptIn: boolean };
  onSignedIn: () => void;
}) {
  const googleSignIn = useAuth((s) => s.googleSignIn);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!googleAvailable()) return null;
  return (
    <div className="mt-6">
      <div className="mb-5 flex items-center gap-3 text-[16px] text-slate-500" aria-hidden>
        <span className="h-px flex-1 bg-slate-200" />
        או
        <span className="h-px flex-1 bg-slate-200" />
      </div>
      <div className={busy ? 'pointer-events-none opacity-60' : ''}>
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={async (res) => {
              if (!res.credential) return setError(GOOGLE_FAILED);
              setBusy(true);
              setError('');
              const ok = await googleSignIn(res.credential, lead);
              setBusy(false);
              if (ok) return onSignedIn();
              const code = useAuth.getState().errorCode;
              setError((code && GOOGLE_ERRORS[code]) || GOOGLE_FAILED);
            }}
            onError={() => setError(GOOGLE_FAILED)}
            text="continue_with"
            shape="pill"
            width={300}
          />
        </div>
      </div>
      {error && (
        <p className="mt-3 text-start text-[16px] leading-relaxed text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** What went wrong, in the visitor's words, and what to do. */
function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK') return NETWORK_MESSAGE;
    if (err.status === 429) return 'כבר שלחנו כמה קישורים בשעה האחרונה. הציצו במייל, או נסו שוב בעוד כשעה.';
    if (err.code === 'RESIDENT_BOT_CHECK') return 'בדיקת האבטחה לא עברה. נסו שוב.';
    if (err.code === 'RESIDENT_NOTICE_CHANGED') return 'הודעת הפרטיות התעדכנה. עברו עליה ושלחו שוב.';
  }
  return 'לא הצלחנו לשלוח כרגע. נסו שוב בעוד רגע.';
}

const RESEND_WAIT_S = 30;

/** The one field and the one button, then "check your email" in the same place. */
function FrontDoor({
  situation,
  intake,
  operator,
  hint,
  next,
  onNoticeChanged,
  onSignedIn,
}: {
  situation: Situation;
  intake: LeadIntake | null;
  operator: string | null;
  hint: string | null;
  next: string;
  onNoticeChanged: () => void;
  onSignedIn: () => void;
}) {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'form' | 'sending' | 'sent'>('form');
  // The address itself (marks the field) vs. a send that did not go through (does not).
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [website, setWebsite] = useState('');
  const [botToken, setBotToken] = useState<string | null>(null);
  const [botReset, setBotReset] = useState(0);
  const [wait, setWait] = useState(0);
  const [resent, setResent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const sentRef = useRef<HTMLHeadingElement>(null);
  const prospect = !!intake;
  const needsBot = prospect && !!turnstileSiteKey();
  const suggestion = suggestEmail(email);
  const copy = COPY[situation];

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  useEffect(() => {
    if (phase === 'sent') sentRef.current?.focus();
  }, [phase]);

  async function send(again = false): Promise<void> {
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setFieldError(value ? 'משהו חסר בכתובת. היא אמורה להיראות כך: name@example.com' : 'הזינו את המייל שלכם');
      inputRef.current?.focus();
      return;
    }
    if (needsBot && !botToken) {
      setError('רגע, בדיקת האבטחה עוד לא הסתיימה. נסו שוב בעוד שנייה.');
      return;
    }
    setError('');
    setFieldError('');
    setResent(false);
    if (!again) setPhase('sending');
    try {
      await api.requestLink(
        value,
        next,
        intake
          ? {
              o: hint ?? undefined,
              consentVersion: intake.consentVersion,
              marketingOptIn: marketing,
              ...(botToken ? { turnstileToken: botToken } : {}),
              website,
            }
          : hint
            ? { o: hint, website }
            : { website },
      );
      navigator.vibrate?.([30, 50, 30]);
      setPhase('sent');
      setWait(RESEND_WAIT_S);
      if (again) setResent(true);
    } catch (err) {
      setPhase(again ? 'sent' : 'form');
      setError(messageFor(err));
      if (err instanceof ApiError && err.code === 'RESIDENT_NOTICE_CHANGED') onNoticeChanged();
      if (!again) inputRef.current?.focus();
    } finally {
      if (needsBot) {
        setBotToken(null);
        setBotReset((n) => n + 1);
      }
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void send();
  }

  const bot = needsBot && <Turnstile lang="he" action="portal_link" onToken={setBotToken} resetKey={botReset} />;

  if (phase === 'sent') {
    return (
      <section className="w-full text-start" aria-live="polite">
        <div className="mb-4 flex h-14 w-14 animate-[pop_.45s_ease-out] items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          <MailCheck className="h-7 w-7" aria-hidden />
        </div>
        <h1 ref={sentRef} tabIndex={-1} className="text-[26px] font-bold leading-tight text-brand-900 outline-none">
          בדקו את המייל
        </h1>
        <div className="mt-2 text-[17px] leading-relaxed text-slate-700" role="status">
          <p>{prospect ? 'שלחנו קישור אישי לכתובת:' : 'אם הכתובת הזו מוכרת לצוות הדיור, קישור חדש כבר בדרך אליה:'}</p>
          {/* The address reads left to right, on the page's own (right) side. */}
          <p className="mt-1 break-all font-semibold text-brand-900">
            <bdi dir="ltr">{email.trim()}</bdi>
          </p>
          {prospect && <p className="mt-1">פתחו אותו מהמייל, והכול יחכה לכם שם.</p>}
        </div>
        <p className="mt-3 text-[16px] leading-relaxed text-slate-500">
          לא רואים אותו? הציצו בספאם או בתיקיית קידומי המכירות.
        </p>
        {bot}
        {error && <SendError text={error} />}
        {resent && (
          <p className="mt-4 text-[16px] font-semibold text-brand-700" role="status">
            שלחנו שוב.
          </p>
        )}
        <button
          type="button"
          onClick={() => void send(true)}
          disabled={wait > 0}
          className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-xl border-2 border-brand-600 bg-white px-4 text-[16px] font-semibold text-brand-700 disabled:border-slate-200 disabled:text-slate-500"
        >
          {wait > 0 ? `אפשר לשלוח שוב בעוד ${wait} שניות` : 'שלחו לי שוב'}
        </button>
        <button
          type="button"
          onClick={() => {
            setPhase('form');
            setError('');
            setFieldError('');
            setResent(false);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className="mt-2 min-h-[48px] w-full text-[16px] font-semibold text-slate-600 underline-offset-4 hover:underline"
        >
          שינוי כתובת המייל
        </button>
        <style>{`@keyframes pop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}`}</style>
      </section>
    );
  }

  const sending = phase === 'sending';
  return (
    <div className="w-full text-start">
      <h1 className="text-[26px] font-bold leading-tight text-brand-900">{copy.title}</h1>
      <p className="mt-2 text-[17px] leading-relaxed text-slate-600">{copy.lead}</p>

      <form onSubmit={submit} noValidate className="mt-6">
        <label htmlFor="front-door-email" className="mb-1.5 block text-[16px] font-semibold text-slate-700">
          המייל שלכם
        </label>
        <input
          ref={inputRef}
          id="front-door-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="send"
          dir="ltr"
          maxLength={200}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (fieldError) setFieldError('');
          }}
          aria-invalid={!!fieldError}
          aria-describedby={fieldError ? 'front-door-error' : undefined}
          placeholder="name@example.com"
          className="min-h-[52px] w-full rounded-xl border border-slate-300 bg-white px-4 text-[17px] outline-none transition-colors placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 aria-[invalid=true]:border-red-400"
        />
        {suggestion && (
          <p className="mt-2 text-[16px] text-slate-600">
            התכוונתם ל-
            <button
              type="button"
              onClick={() => setEmail(suggestion)}
              className="min-h-[44px] font-semibold text-brand-700 underline underline-offset-2"
              dir="ltr"
            >
              {suggestion}
            </button>
            ?
          </p>
        )}
        {fieldError && <ErrorLine id="front-door-error" text={fieldError} />}

        {/* Honeypot: hidden from people and screen readers; bots fill it. */}
        <div aria-hidden="true" className="absolute -start-[9999px] h-px w-px overflow-hidden">
          <label htmlFor="front-door-website">Website</label>
          <input
            id="front-door-website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        {prospect && operator && (
          <label className="mt-4 flex min-h-[48px] cursor-pointer items-start gap-3 text-[16px] leading-relaxed text-slate-600">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-1 h-6 w-6 shrink-0 accent-brand-600"
            />
            <span>אשמח לקבל {withPrefix('מ', operator)} עדכונים על דירות שמתפנות (לא חובה)</span>
          </label>
        )}

        {bot}
        {error && <SendError text={error} />}

        <button
          type="submit"
          disabled={sending}
          className="mt-5 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-[17px] font-semibold text-white shadow-sm transition-transform active:scale-[0.99] disabled:opacity-70"
        >
          {sending && <Loader2 className="h-5 w-5 animate-spin" aria-hidden />}
          {sending ? 'שולחים…' : 'שלחו לי את הקישור'}
        </button>
      </form>

      {prospect && (
        <p className="mt-3 text-[16px] leading-relaxed text-slate-600">כבר גרים אצלנו? אותו מייל, אותו קישור.</p>
      )}

      {operator && intake && <PrivacyNote operator={operator} intake={intake} />}

      <GoogleOption
        lead={intake ? { consentVersion: intake.consentVersion, marketingOptIn: marketing } : undefined}
        onSignedIn={onSignedIn}
      />
    </div>
  );
}

/**
 * The notice at collection (Israeli Privacy Protection Law): who holds the
 * data and who processes it, what for, that giving it is voluntary, and when
 * it is deleted. One calm line, the rest one tap away. Its wording is versioned
 * on the server (PORTAL_LEAD_CONSENT_VERSION in portal-lead.service.ts): change
 * this text and bump that version together.
 */
function PrivacyNote({ operator, intake }: { operator: string; intake: LeadIntake }) {
  const hours = Math.max(1, Math.round(intake.pendingMinutes / 60));
  const within = hours === 2 ? 'שעתיים' : `${hours} שעות`;
  return (
    <div className="mt-6 border-t border-slate-200 pt-4 text-[16px] leading-relaxed text-slate-500">
      <p>
        המייל נשמר אצל {operator} ומשמש לשליחת הקישור, לתיאום סיור ולמענה לפניות שלכם.{' '}
        <span className="whitespace-nowrap">TendedLoop</span> מעבדת אותו עבורם.
      </p>
      <details className="group mt-1">
        <summary className="flex min-h-[48px] cursor-pointer list-none items-center gap-1 font-semibold text-slate-600 underline-offset-4 hover:underline [&::-webkit-details-marker]:hidden">
          עוד על הפרטיות
          <ChevronDown className="h-5 w-5 transition-transform group-open:rotate-180" aria-hidden />
        </summary>
        <div className="space-y-2 pb-2">
          <p>
            {operator} הם בעלי המאגר והאחראים למידע, <span className="whitespace-nowrap">ו-TendedLoop</span> מעבדת אותו
            בשמם. אין חובה חוקית למסור את המייל, אבל בלעדיו לא נוכל לשלוח לכם את הקישור.
          </p>
          <p>
            אם הקישור לא ייפתח תוך {within}, המייל נמחק. אם לא תמשיכו לחוזה, הפרטים נמחקים אחרי 90 יום. עדכונים שיווקיים
            יישלחו רק אם סימנתם זאת, ואפשר להפסיק אותם בכל עת.
          </p>
          {intake.privacyPolicyUrl && (
            <a
              href={intake.privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[48px] items-center font-semibold text-brand-700 underline"
            >
              מדיניות הפרטיות של {operator}
            </a>
          )}
        </div>
      </details>
    </div>
  );
}

/** A send that did not go through: what happened and what to do, beside the button (the address is fine). */
function SendError({ text }: { text: string }) {
  return (
    <p
      className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-[16px] leading-relaxed text-red-800"
      role="alert"
    >
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      {text}
    </p>
  );
}

function ErrorLine({ text, id }: { text: string; id?: string }) {
  return (
    <p id={id} className="mt-2 text-[16px] leading-relaxed text-red-700" role="alert">
      {text}
    </p>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const expired = params.get('expired') === '1';
  // Only an internal path (e.g. "/lease"), and the org hint goes along.
  const hint = useBrand((s) => s.hintHandle);
  const nextPath = safeNext(params.get('next'));
  const next = afterLoginPath(nextPath, hint);
  const { verifyLink, loading, error, isAuthenticated } = useAuth();
  const started = useRef(false);
  const [noticeReload, setNoticeReload] = useState(0);
  const intake = useLeadIntake(hint, noticeReload);

  // With a link in the URL, move on only once THAT link has verified: a session
  // already on this device must not carry the resident past a different link.
  // Its office's brand decides this page; asked for only when the link did not
  // open the portal (it then still names its office, valid or expired).
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    tokenPending();
    void verifyLink(token).then((ok) => {
      if (ok) navigate(next, { replace: true });
      else void loadTokenBrand(token);
    });
  }, [token, verifyLink, navigate, next]);
  useEffect(() => () => clearToken(), []);

  useEffect(() => {
    if (!token && isAuthenticated) navigate(next, { replace: true });
  }, [token, isAuthenticated, navigate, next]);

  const verifying = !!token && (loading || (!error && !isAuthenticated));
  const linkFailed = !!token && !loading && !!error;
  // The link's office, else a returning resident's building (kept on this
  // device after a visit), else the link's org hint.
  const brand = useBrand((s) => s.brand);
  const brandLoading = useBrand((s) => s.loading);
  const banded = !!brand || brandLoading;
  const lead = intake.state === 'ready' ? intake.intake : null;
  // Prospect copy only for the office the brand on screen belongs to.
  const operator = brand?.displayName?.trim() || null;
  const situation: Situation = linkFailed ? 'invalid' : expired ? 'expired' : lead ? 'prospect' : 'resident';
  const deciding = !verifying && intake.state === 'loading';

  return (
    <div className="min-h-screen">
      {brand ? <BrandBand brand={brand} size="hero" /> : brandLoading && <BandSkeleton />}
      <div className={'mx-auto flex max-w-md flex-col px-5 ' + (banded ? 'pb-10 pt-2' : 'min-h-screen pb-10 pt-14')}>
        {!banded && (
          <div className="mb-8 flex items-center gap-2.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500 text-white shadow-md">
              <Home className="h-6 w-6" aria-hidden />
            </div>
            <p className="text-lg font-bold tracking-tight text-brand-900" dir="ltr">
              TendedLoop
            </p>
          </div>
        )}

        {verifying && (
          <div className="flex flex-col items-center py-10 text-center" role="status">
            <Loader2 className="mb-3 h-7 w-7 animate-spin text-brand-500" aria-hidden />
            <p className="text-lg text-brand-900">פותחים את הקישור…</p>
          </div>
        )}

        {deciding && (
          <div className="w-full animate-pulse space-y-3 pt-1" aria-busy="true" data-testid="front-door-skeleton">
            <div className="h-8 w-4/5 rounded-lg bg-slate-100" />
            <div className="h-5 w-full rounded bg-slate-100" />
            <div className="h-5 w-3/5 rounded bg-slate-100" />
            <div className="!mt-8 h-[52px] w-full rounded-xl bg-slate-100" />
            <div className="h-[52px] w-full rounded-xl bg-slate-200" />
          </div>
        )}

        {!verifying && !deciding && (
          <FrontDoor
            key={`${situation}:${hint ?? ''}`}
            situation={situation}
            intake={lead && operator ? lead : null}
            operator={operator}
            hint={hint}
            next={nextPath}
            onNoticeChanged={() => setNoticeReload((n) => n + 1)}
            onSignedIn={() => navigate(next, { replace: true })}
          />
        )}

        {!verifying && (
          <p className="mt-8 flex items-center gap-1.5 text-[16px] text-slate-500">
            <Lock className="h-4 w-4 shrink-0" aria-hidden /> הפרטים שלכם נשמרים מוצפנים ומאובטחים
          </p>
        )}
        {/* The app is TendedLoop's; the band above is the building's. */}
        {brand && (
          <p className="mt-3 text-[16px] text-slate-500">
            <b className="font-bold text-slate-800" dir="ltr">
              TendedLoop
            </b>{' '}
            · פורטל הדיירים
          </p>
        )}
      </div>
    </div>
  );
}
